import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CommunicationTemplateDTO,
  CommunicationTemplatePreviewDTO,
  CommunicationVarDTO,
  CreateCommunicationTemplateRequest,
  PreviewCommunicationTemplateRequest,
  WrapperPageableResponseCommunicationTemplateDTO,
  WrapperResponse,
  WrapperResponseCommunicationTemplateDTO,
  WrapperResponseCommunicationTemplatePreviewDTO,
  WrapperResponseListCommunicationVarDTO,
  WrapperResponseListReadFileResponse,
  WrapperResponseReadFileResponse,
  AttachCommunicationTemplateFileRequest
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class CommunicationTemplateService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/communication-templates`;

  search(
    params: Record<string, unknown> = {},
    pageable: { page?: number; size?: number; sort?: string[] } = {}
  ): Observable<WrapperPageableResponseCommunicationTemplateDTO> {
    return this.http.get<WrapperPageableResponseCommunicationTemplateDTO>(this.apiUrl, {
      params: this.buildParams({ ...params, ...pageable })
    });
  }

  list(
    params: Record<string, unknown> = {},
    pageable: { page?: number; size?: number; sort?: string[] } = {}
  ): Observable<WrapperPageableResponseCommunicationTemplateDTO> {
    return this.http.get<WrapperPageableResponseCommunicationTemplateDTO>(`${this.apiUrl}/list`, {
      params: this.buildParams({ ...params, ...pageable })
    });
  }

  getById(id: number): Observable<WrapperResponseCommunicationTemplateDTO> {
    return this.http.get<WrapperResponseCommunicationTemplateDTO>(`${this.apiUrl}/${id}`);
  }

  listVars(type: string): Observable<WrapperResponseListCommunicationVarDTO> {
    const params = new HttpParams().set('type', type);
    return this.http.get<WrapperResponseListCommunicationVarDTO>(`${this.apiUrl}/vars`, { params });
  }

  create(request: CreateCommunicationTemplateRequest): Observable<WrapperResponseCommunicationTemplateDTO> {
    return this.http.post<WrapperResponseCommunicationTemplateDTO>(this.apiUrl, request);
  }

  update(id: number, request: CreateCommunicationTemplateRequest): Observable<WrapperResponseCommunicationTemplateDTO> {
    return this.http.put<WrapperResponseCommunicationTemplateDTO>(`${this.apiUrl}/${id}`, request);
  }

  delete(id: number): Observable<WrapperResponse<void>> {
    return this.http.delete<WrapperResponse<void>>(`${this.apiUrl}/${id}`);
  }

  preview(request: PreviewCommunicationTemplateRequest): Observable<WrapperResponseCommunicationTemplatePreviewDTO> {
    return this.http.post<WrapperResponseCommunicationTemplatePreviewDTO>(`${this.apiUrl}/preview`, request);
  }

  listAttachments(
    templateId: number,
    spec: { 'name[lk]'?: string } = {}
  ): Observable<WrapperResponseListReadFileResponse> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return this.http.get<WrapperResponseListReadFileResponse>(
      `${this.apiUrl}/${templateId}/attachments`,
      { params }
    );
  }

  attachFile(
    templateId: number,
    request: AttachCommunicationTemplateFileRequest
  ): Observable<WrapperResponseReadFileResponse> {
    return this.http.post<WrapperResponseReadFileResponse>(
      `${this.apiUrl}/${templateId}/attach`,
      request
    );
  }

  deleteAttachment(templateId: number, fileId: number): Observable<WrapperResponse<void>> {
    return this.http.delete<WrapperResponse<void>>(
      `${this.apiUrl}/${templateId}/attachments/${fileId}`
    );
  }

  private buildParams(obj: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (value == null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((v) => { params = params.append(key, String(v)); });
      } else {
        params = params.append(key, String(value));
      }
    });
    return params;
  }
}

export type {
  CommunicationTemplateDTO,
  CommunicationTemplatePreviewDTO,
  CommunicationVarDTO,
  CreateCommunicationTemplateRequest,
  PreviewCommunicationTemplateRequest
};
